import React from 'react';
import { ArrowRight, Info } from 'lucide-react';
const SystemHealthRadar = () => {
  const cx = 120;
  const cy = 120;
  const R = 84;
  const domains = [{
    key: 'Control',
    value: 47.9,
    angle: -90
  }, {
    key: 'Attention',
    value: 52.7,
    angle: 0
  }, {
    key: 'Prediction',
    value: 59.3,
    angle: 90
  }, {
    key: 'Resilience',
    value: 44.9,
    angle: 180
  }];
  const valueColor = (v: number) => v >= 65 ? '#16a34a' : v >= 50 ? '#d97706' : '#dc2626';
  const toXY = (angleDeg: number, radius: number) => {
    const rad = angleDeg * Math.PI / 180;
    return {
      x: cx + Math.cos(rad) * radius,
      y: cy + Math.sin(rad) * radius
    };
  };
  const dataPoints = domains.map(d => {
    const p = toXY(d.angle, d.value / 100 * R);
    return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
  }).join(' ');
  return <svg viewBox="0 0 240 240" className="w-full max-w-[260px] mx-auto" role="img" aria-label="System health radar: Control 47.9, Attention 52.7, Prediction 59.3, Resilience 44.9" data-id="element-481" data-mp="1r1lqh75xc6ki">
      {/* Grid rings */}
      {[0.25, 0.5, 0.75, 1].map(f => <circle key={f} cx={cx} cy={cy} r={R * f} fill="none" stroke="#e5e7eb" strokeWidth={1} data-id="element-482" data-mp="1goyq434rxayl" />)}
      {/* Axis lines */}
      {domains.map(d => {
      const p = toXY(d.angle, R);
      return <line key={d.key} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#e5e7eb" strokeWidth={1} data-id="element-483" data-mp="16zf5ot1ex91m" />;
    })}
      {/* Data polygon */}
      <polygon points={dataPoints} fill="rgba(79,70,229,0.15)" stroke="#4f46e5" strokeWidth={2} data-id="element-484" data-mp="sze3na9evvkk" />
      {/* Data dots + axis labels */}
      {domains.map(d => {
      const dp = toXY(d.angle, d.value / 100 * R);
      const lp = toXY(d.angle, R + 22);
      return <g key={d.key} data-id="element-485" data-mp="k1byciinagrh">
            <circle cx={dp.x} cy={dp.y} r={3.5} fill="#4f46e5" data-id="element-486" data-mp="o015vuh05437" />
            <text x={lp.x} y={lp.y - 4} textAnchor="middle" className="fill-gray-500" style={{
          fontSize: 11,
          fontWeight: 600
        }} data-id="element-487" data-mp="o2ngk2z6vzpn">
              {d.key}
            </text>
            <text x={lp.x} y={lp.y + 9} textAnchor="middle" style={{
          fontSize: 12,
          fontWeight: 700,
          fill: valueColor(d.value)
        }} data-id="element-488" data-mp="2ichj5f87tihk">
              {d.value}
            </text>
          </g>;
    })}
    </svg>;
};
const TeamNeuro = () => {
  const getScoreColor = (score: number) => {
    if (score >= 65) return 'text-green-600';
    if (score >= 50) return 'text-amber-500';
    return 'text-red-600';
  };
  const getBarColor = (score: number) => {
    if (score >= 65) return 'bg-green-600';
    if (score >= 50) return 'bg-amber-500';
    return 'bg-red-600';
  };
  const getDotColor = (score: number | string) => {
    if (typeof score === 'string') return 'bg-gray-200';
    if (score >= 65) return 'bg-green-500';
    if (score >= 50) return 'bg-amber-500';
    return 'bg-gray-400';
  };
  const roster = [{
    name: 'Player A',
    key: true,
    pos: 'G',
    min: 28,
    ctrl: 72,
    att: 68,
    pred: 75,
    res: 52,
    archetype: 'Floor Reader'
  }, {
    name: 'Player B',
    key: true,
    pos: 'G',
    min: 25,
    ctrl: 58,
    att: 71,
    pred: 69,
    res: 41,
    archetype: 'Game Manager'
  }, {
    name: 'Player C',
    key: true,
    pos: 'SF',
    min: 22,
    ctrl: 61,
    att: 55,
    pred: 67,
    res: 48,
    archetype: 'Def. Disruptor'
  }, {
    name: 'Player D',
    key: true,
    pos: 'PF',
    min: 20,
    ctrl: 49,
    att: 52,
    pred: 65,
    res: 62,
    archetype: 'Impact Stab.'
  }, {
    name: 'Player E',
    key: true,
    pos: 'C',
    min: 18,
    ctrl: 38,
    att: 48,
    pred: 58,
    res: 67,
    archetype: 'Rim Impact'
  }, {
    name: 'Player F',
    key: false,
    pos: 'G',
    min: 16,
    ctrl: 55,
    att: 60,
    pred: 44,
    res: 43,
    archetype: 'Pressure Hld.'
  }, {
    name: 'Player G',
    key: false,
    pos: 'SG',
    min: 14,
    ctrl: 42,
    att: 51,
    pred: 52,
    res: 38,
    archetype: 'Game Manager'
  }, {
    name: 'Player H',
    key: false,
    pos: 'C',
    min: 12,
    ctrl: 27,
    att: 44,
    pred: 48,
    res: 56,
    archetype: 'Rebound Ant.'
  }, {
    name: 'Player I',
    key: false,
    pos: 'SF',
    min: 10,
    ctrl: 35,
    att: 49,
    pred: 51,
    res: 31,
    archetype: 'Clutch Perf.'
  }, {
    name: 'Player J',
    key: false,
    pos: 'G',
    min: 7,
    ctrl: 29,
    att: 40,
    pred: 38,
    res: 28,
    archetype: 'Scoring Init.'
  }, {
    name: 'Player K',
    key: false,
    pos: 'PF',
    min: 5,
    ctrl: 32,
    att: 46,
    pred: 44,
    res: 35,
    archetype: 'Game Manager'
  }, {
    name: 'Player L',
    key: false,
    pos: 'G',
    min: 3,
    ctrl: '-',
    att: '-',
    pred: '-',
    res: '-',
    archetype: 'Insufficient data (≤5 min)'
  }];
  return <div className="w-full text-gray-900 font-sans bg-[#F9FAFB]" data-id="element-489" data-mp="1f6gk4rw848a9">
      {/* SECTION 1: TEAM IDENTITY */}
      <section className="mb-6" data-id="element-490" data-mp="2vkgg5rry7ds8">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-491" data-mp="2vjonoptndw66">
          {/* Card header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-492" data-mp="2qoiyni4y81dv">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-493" data-mp="cgqgoxvprmu0">
              Team identity
            </h2>
            <span className="text-xs text-gray-400 lowercase tracking-wide" data-id="element-494" data-mp="3dpeh89yyfvm">
              cognitive archetype
            </span>
          </div>

          {/* Two columns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 px-6 py-5" data-id="element-495" data-mp="8ewkp2wgr706">
            {/* LEFT column: archetype + team-level status */}
            <div data-id="element-496" data-mp="104hsm0dcdxur">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5" data-id="element-497" data-mp="102x0siiewt0l">
                Cognitive archetype
              </div>
              <h3 className="text-3xl font-bold text-gray-900 tracking-tight mb-2" data-id="element-498" data-mp="rboa7u2lmico">
                Floor Reading System
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-6" data-id="element-499" data-mp="1f07i48hqkt48">
                Dominant signature: reading + anticipation. Gaps: resilience
                endurance.
              </p>

              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2.5" data-id="element-500" data-mp="1tmwgu845z29k">
                Team-level status
              </div>
              <div className="flex flex-wrap gap-2 mb-3" data-id="element-501" data-mp="1jprtw62hv2rp">
                <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-green-50 border border-green-200 text-xs font-medium text-green-700 cursor-help" title="Structural — 3 or more players score ≥65. A distributed, reliable team-level strength." data-id="element-502" data-mp="1jo6xeawmd9r7">
                  Prediction · structural
                </span>
                <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-amber-50 border border-amber-200 text-xs font-medium text-amber-700 cursor-help" title="Dependency — only 1 player scores ≥65. The team relies on a single individual for that capability." data-id="element-503" data-mp="15wad2t17045d">
                  Control · dependency
                </span>
                <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-amber-50 border border-amber-200 text-xs font-medium text-amber-700 cursor-help" title="Dependency — only 1 player scores ≥65. The team relies on a single individual for that capability." data-id="element-504" data-mp="myzd167ql6ov">
                  Attention · dependency
                </span>
                <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-red-50 border border-red-200 text-xs font-medium text-red-700 cursor-help" title="Critical — domain below ~45 with only 1 player ≥65. A systemic fragility requiring urgent attention." data-id="element-505" data-mp="37o2d3bf4l7k5">
                  Resilience · critical
                </span>
              </div>
              <p className="text-gray-400 text-[12px]" data-id="element-506" data-mp="2oxb4wd6vjn7c">
                Structural = 3+ players ≥65 · Dependency = 1 player ≥65 ·
                Critical = fragile
              </p>

              {/* Cognitive sequence */}
              <div className="mt-6 pt-5 border-t border-gray-100" data-id="element-507" data-mp="1w7cd4a1apbyz">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-3" data-id="element-508" data-mp="21773mch5rzji">
                  Cognitive sequence{' '}
                  <span className="text-gray-300" data-id="element-509" data-mp="ukxrajdzx39q">
                    — algorithmically generated · primary
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2.5 mb-4" data-id="element-510" data-mp="13kksp56q692s">
                  <span className="px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-md text-sm font-semibold text-indigo-700" data-id="element-511" data-mp="13igdhr3dlnn7">
                    Court scan
                  </span>
                  <ArrowRight className="w-4 h-4 text-gray-400" data-id="element-512" data-mp="1dgw3trukwsvn" />
                  <span className="px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-md text-sm font-semibold text-indigo-700" data-id="element-513" data-mp="18j4e94hepium">
                    Advantage ID
                  </span>
                  <ArrowRight className="w-4 h-4 text-gray-400" data-id="element-514" data-mp="1ias0mqz3w9p8" />
                  <span className="px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-md text-sm font-semibold text-indigo-700" data-id="element-515" data-mp="1w98hhaqongl3">
                    Anticipatory pressure
                  </span>
                </div>

                {/* Alternative sequences — separate row */}
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2" data-id="element-516" data-mp="2fva13pkf8mqd">
                  Alternative sequences
                </div>
                <div className="flex flex-wrap items-center gap-2.5" data-id="element-517" data-mp="20xhl6waypwbn">
                  <span className="px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-md text-xs font-medium text-gray-500 cursor-help" title="Perimeter lock — collapse the perimeter early to force the ball inside, betting on contained interior reads." data-id="element-518" data-mp="25wazgqy2prcv">
                    Perimeter lock
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 text-gray-300" data-id="element-519" data-mp="1i8ugwg0m1az4" />
                  <span className="px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-md text-xs font-medium text-gray-500 cursor-help" title="Lane intercept — sit on passing lanes to convert defensive reads into transition opportunities." data-id="element-520" data-mp="1n630ml1epn2h">
                    Lane intercept
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 text-gray-300" data-id="element-521" data-mp="2o19ssjaiayac" />
                  <span className="px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-md text-xs font-medium text-gray-500 cursor-help" title="Transition push — accelerate after a stop to attack before the defense is set, leaning on early advantage." data-id="element-522" data-mp="2t0mh78h10df1">
                    Transition push
                  </span>
                </div>
              </div>
            </div>

            {/* RIGHT column: radar only */}
            <div className="lg:border-l lg:border-gray-100 lg:pl-8 flex flex-col justify-center" data-id="element-523" data-mp="3brnapgzdngr7">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1" data-id="element-524" data-mp="2y08ufhwuttnu">
                System health
              </div>
              <SystemHealthRadar data-id="element-525" data-mp="31zhoknvt3tjx" />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: SYSTEM HEALTH */}
      <section className="mb-6" data-id="element-526" data-mp="3uhd6o7evm8kp">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-527" data-mp="3uin4cvhtheqt">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-528" data-mp="1j1l1q3beza9o">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-529" data-mp="1e2i7zf0vwqh0">
              System health
            </h2>
            <span className="text-[11px] text-gray-400" data-id="element-530" data-mp="1sr4m5derzc6n">
              All values on a 0–100 scale · percentiles vs current league
              database
            </span>
          </div>
          <div className="grid grid-cols-12 gap-8 px-6 py-5" data-id="element-531" data-mp="104tjlx7lz177">
            <div className="col-span-5 space-y-4" data-id="element-532" data-mp="v4p5uey34kko">
              {/* Domain Rows */}
              <div className="flex items-center text-sm" data-id="element-533" data-mp="v71vlea33z8a">
                <div className="w-24 font-medium text-gray-700" data-id="element-534" data-mp="1a2pt6kkwdo37">Control</div>
                <div className="w-10 font-bold text-amber-600 text-right mr-4" data-id="element-535" data-mp="152ln7ohm8br8">
                  47.9
                </div>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden mr-4" data-id="element-536" data-mp="2lfsefsxxz1tp">
                  <div className="h-full bg-amber-500" style={{
                  width: '47.9%'
                }} data-id="element-537" data-mp="2gh8mnbwo2vtf"></div>
                </div>
                <div className="w-24 text-xs text-amber-600" data-id="element-538" data-mp="2nnktresjmbwz">Dependency</div>
              </div>
              <div className="flex items-center text-sm" data-id="element-539" data-mp="2sm4j7p6ci5u3">
                <div className="w-24 font-medium text-gray-700" data-id="element-540" data-mp="2xkpldu55qzl6">Attention</div>
                <div className="w-10 font-bold text-amber-600 text-right mr-4" data-id="element-541" data-mp="32ir2wamxqquj">
                  52.7
                </div>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden mr-4" data-id="element-542" data-mp="24w1wzpp43u3e">
                  <div className="h-full bg-amber-500" style={{
                  width: '52.7%'
                }} data-id="element-543" data-mp="29ulgzkk5avt4"></div>
                </div>
                <div className="w-24 text-xs text-amber-600" data-id="element-544" data-mp="2er24ccjit655">Dependency</div>
              </div>
              <div className="flex items-center text-sm" data-id="element-545" data-mp="2jlg5ywos09i9">
                <div className="w-24 font-medium text-gray-700" data-id="element-546" data-mp="1m3g1j6i4a5jc">Prediction</div>
                <div className="w-10 font-bold text-green-600 text-right mr-4" data-id="element-547" data-mp="1r2iv9usncpc0">
                  59.3
                </div>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden mr-4" data-id="element-548" data-mp="87o5vv6elasz">
                  <div className="h-full bg-green-600" style={{
                  width: '59.3%'
                }} data-id="element-549" data-mp="47xkk6znbmvu"></div>
                </div>
                <div className="w-24 text-xs text-green-600" data-id="element-550" data-mp="3veqkz8mm7hzb">Structural</div>
              </div>
              <div className="flex items-center text-sm" data-id="element-551" data-mp="3qg6uqw0znk15">
                <div className="w-24 font-medium text-gray-700" data-id="element-552" data-mp="3lfj5ofwbld9k">Resilience</div>
                <div className="w-10 font-bold text-red-600 text-right mr-4" data-id="element-553" data-mp="3gnrcdxls4tfq">
                  44.9
                </div>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden mr-4" data-id="element-554" data-mp="3cn7f8u17hptl">
                  <div className="h-full bg-red-600" style={{
                  width: '44.9%'
                }} data-id="element-555" data-mp="37omfswts3a67"></div>
                </div>
                <div className="w-24 text-xs text-red-600" data-id="element-556" data-mp="32q2riprppmao">Critical</div>
              </div>
            </div>

            <div className="col-span-7 grid grid-cols-2 gap-6" data-id="element-557" data-mp="11m0l16ew2s3d">
              <div className="bg-green-50/50 border border-green-100 p-4 rounded-lg" data-id="element-558" data-mp="11msikms1qzwa">
                <h4 className="text-xs font-bold text-green-800 uppercase tracking-wider mb-3" data-id="element-559" data-mp="16jsggaeqswcb">
                  Team-level strengths
                </h4>
                <ul className="space-y-2 text-sm text-gray-700" data-id="element-560" data-mp="wv0qu8x5mo4k">
                  <li className="flex items-start" data-id="element-561" data-mp="wt7humz2jrte">
                    <span className="text-green-500 mr-2 mt-0.5" data-id="element-562" data-mp="iv9eecmadfso">•</span>
                    <span data-id="element-563" data-mp="nucbnaw0yhq3">
                      Reading at{' '}
                      <strong className="font-bold text-green-700" data-id="element-564" data-mp="nvx32rn1lkjq">
                        100th percentile
                      </strong>{' '}
                      —{' '}
                      <strong className="font-bold" data-id="element-565" data-mp="dx7lz12z044j">
                        best floor reading in the league
                      </strong>
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-566" data-mp="289e8ux5889co">
                    <span className="text-green-500 mr-2 mt-0.5" data-id="element-567" data-mp="2883690nip7n5">•</span>
                    <span data-id="element-568" data-mp="3ssriiwk4tztr">
                      Interception at{' '}
                      <strong className="font-bold text-green-700" data-id="element-569" data-mp="3nug9go1nvtcl">
                        100th percentile
                      </strong>{' '}
                      — collective perimeter reading,{' '}
                      <strong className="font-bold" data-id="element-570" data-mp="1l5h7akvy9g2">
                        systematic lane interception
                      </strong>
                    </span>
                  </li>
                </ul>
              </div>
              <div className="bg-red-50/50 border border-red-100 p-4 rounded-lg" data-id="element-571" data-mp="bbqg6z4h1fzi">
                <h4 className="text-xs font-bold text-red-800 uppercase tracking-wider mb-3" data-id="element-572" data-mp="pagfmlerjrr1">
                  Team-level vulnerabilities
                </h4>
                <ul className="space-y-2 text-sm text-gray-700" data-id="element-573" data-mp="2r2w93bqkzpzu">
                  <li className="flex items-start" data-id="element-574" data-mp="2r58mdble9c6k">
                    <span className="text-red-500 mr-2 mt-0.5" data-id="element-575" data-mp="2m4uk6a6h4wgg">•</span>
                    <span data-id="element-576" data-mp="q16dxmp0g7wm">
                      Resilience{' '}
                      <strong className="font-bold text-red-600" data-id="element-577" data-mp="q2rcdneg4azr">44.9</strong> —
                      endurance{' '}
                      <strong className="font-bold" data-id="element-578" data-mp="v1udiwrb1x2g">not distributed</strong>,{' '}
                      <strong className="font-bold" data-id="element-579" data-mp="z0a0x1rat7ge">1 player</strong> carries
                      the load
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-580" data-mp="190kc6gtrw4sx">
                    <span className="text-red-500 mr-2 mt-0.5" data-id="element-581" data-mp="18xy66lmj5xct">•</span>
                    <span data-id="element-582" data-mp="1dxar30tucucr">
                      Load balance{' '}
                      <strong className="font-bold text-red-600" data-id="element-583" data-mp="1ipbzgbnw20xi">27.1</strong> —{' '}
                      <strong className="font-bold" data-id="element-584" data-mp="1mqxk4j6nzji9">critically low</strong>,
                      primary scouting priority
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: DOMAIN ANALYSIS */}
      <section className="mb-6" data-id="element-585" data-mp="26tk0bpqaxyot">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-586" data-mp="26uv2jl43mydt">
          <div className="flex items-center px-6 py-4 border-b border-gray-200" data-id="element-587" data-mp="21sm80kbkvo9l">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-588" data-mp="1ww5ipmsn73ux">
              Domain analysis
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-6 px-6 py-5" data-id="element-589" data-mp="1svbu0r7dll0z">
            {/* Control */}
            <div className="border border-gray-200 rounded-lg p-5" data-id="element-590" data-mp="2pj9ucam8qgfo">
              <div className="flex justify-between items-start mb-6" data-id="element-591" data-mp="2kkf1efuk7bhu">
                <div data-id="element-592" data-mp="2ftym0q76hfsu">
                  <h3 className="text-base font-medium text-gray-900" data-id="element-593" data-mp="2ft6qtghhtk33">
                    Control
                  </h3>
                  <div className="text-xs text-amber-600 mt-1" data-id="element-594" data-mp="2brljsbrn400t">
                    1 player ≥65 · dependency
                  </div>
                </div>
                <div className="text-2xl font-bold text-amber-600" data-id="element-595" data-mp="33jsyv7rhz4d3">47.9</div>
              </div>
              <div className="space-y-3 mb-5" data-id="element-596" data-mp="3n7l7afpd4s6v">
                <div className="flex items-center text-[13px]" data-id="element-597" data-mp="3n6tbp4vrmugl">
                  <div className="w-24 text-gray-600" data-id="element-598" data-mp="3rytgpbqp0dk1">Consistency</div>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full mx-3 overflow-hidden" data-id="element-599" data-mp="3s04kvco1vnbo">
                    <div className="h-full bg-amber-500" style={{
                    width: '61.9%'
                  }} data-id="element-600" data-mp="3d7kiie8q5jva"></div>
                  </div>
                  <div className="w-8 text-right font-medium" data-id="element-601" data-mp="9tq6jclvdbcr">61.9</div>
                </div>
                <div className="flex items-center text-[13px]" data-id="element-602" data-mp="es1hxrrtbtx3">
                  <div className="w-24 text-gray-600" data-id="element-603" data-mp="3vzwwjy8qq9l9">Discipline</div>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full mx-3 overflow-hidden" data-id="element-604" data-mp="3vzn5e7bylwdz">
                    <div className="h-full bg-red-500" style={{
                    width: '28.6%'
                  }} data-id="element-605" data-mp="4rr2e0ib2aer"></div>
                  </div>
                  <div className="w-8 text-right font-medium" data-id="element-606" data-mp="2pkcdlxf7kcts">28.6</div>
                </div>
                <div className="flex items-center text-[13px]" data-id="element-607" data-mp="17lxz3aosx7id">
                  <div className="w-24 text-gray-600" data-id="element-608" data-mp="17oaog8ww2k5g">Play control</div>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full mx-3 overflow-hidden" data-id="element-609" data-mp="12mblh0529p91">
                    <div className="h-full bg-amber-500" style={{
                    width: '53.2%'
                  }} data-id="element-610" data-mp="1gll0hwhj6dbf"></div>
                  </div>
                  <div className="w-8 text-right font-medium" data-id="element-611" data-mp="ovpz31geex6e">53.2</div>
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded text-[13px] text-gray-700 leading-relaxed" data-id="element-612" data-mp="jwvj079tfmpz">
                Continuity at an elite level (Consistency: 61.9, 94th pct);
                execution quality stable. Discipline functional at 72nd pct.
              </div>
            </div>

            {/* Attention */}
            <div className="border border-gray-200 rounded-lg p-5" data-id="element-613" data-mp="56xnf69k3uqr">
              <div className="flex justify-between items-start mb-6" data-id="element-614" data-mp="163zicw46fxv">
                <div data-id="element-615" data-mp="15voyrngrmpu">
                  <h3 className="text-base font-medium text-gray-900" data-id="element-616" data-mp="2rdc2b5ca04u8">
                    Attention
                  </h3>
                  <div className="text-xs text-amber-600 mt-1" data-id="element-617" data-mp="2ncab9bsiiz3c">
                    1 player ≥65 · dependency
                  </div>
                </div>
                <div className="text-2xl font-bold text-amber-600" data-id="element-618" data-mp="3158d9gzgnpfv">52.7</div>
              </div>
              <div className="space-y-3 mb-5" data-id="element-619" data-mp="2wb42emn2qx9i">
                <div className="flex items-center text-[13px]" data-id="element-620" data-mp="3b0tirp3qn7ud">
                  <div className="w-24 text-gray-600" data-id="element-621" data-mp="3b362o7t23sa2">Reading</div>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full mx-3 overflow-hidden" data-id="element-622" data-mp="361qei6vl15ng">
                    <div className="h-full bg-amber-500" style={{
                    width: '60.3%'
                  }} data-id="element-623" data-mp="3k0q178zjbagz"></div>
                  </div>
                  <div className="w-8 text-right font-medium" data-id="element-624" data-mp="1pnqih6zuy05h">60.3</div>
                </div>
                <div className="flex items-center text-[13px]" data-id="element-625" data-mp="1kox4zekkm95b">
                  <div className="w-24 text-gray-600" data-id="element-626" data-mp="1kqqb8sr9urcs">Selection</div>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full mx-3 overflow-hidden" data-id="element-627" data-mp="7wajkk1gexl">
                    <div className="h-full bg-red-500" style={{
                    width: '49.8%'
                  }} data-id="element-628" data-mp="567n431px3ji"></div>
                  </div>
                  <div className="w-8 text-right font-medium" data-id="element-629" data-mp="3nhujaa52eomk">49.8</div>
                </div>
                <div className="flex items-center text-[13px]" data-id="element-630" data-mp="3rjfvdt9ryyvp">
                  <div className="w-24 text-gray-600" data-id="element-631" data-mp="3dkq0mkafhbai">Focus</div>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full mx-3 overflow-hidden" data-id="element-632" data-mp="3ikkpyjek1stg">
                    <div className="h-full bg-red-500" style={{
                    width: '43.3%'
                  }} data-id="element-633" data-mp="34lclpc3jbuie"></div>
                  </div>
                  <div className="w-8 text-right font-medium" data-id="element-634" data-mp="38n6k1dq9cmfe">43.3</div>
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded text-[13px] text-gray-700 leading-relaxed" data-id="element-635" data-mp="12uhmeaa4dd6x">
                Floor reading decent (60.3); functional but not systematic.
                Selection (49.8) and Focus (43.3) need reinforcement.
              </div>
            </div>

            {/* Prediction */}
            <div className="border border-gray-200 rounded-lg p-5" data-id="element-636" data-mp="2av9och6p931j">
              <div className="flex justify-between items-start mb-6" data-id="element-637" data-mp="26sd0aaye4eq7">
                <div data-id="element-638" data-mp="21t0gy06q9psd">
                  <h3 className="text-base font-medium text-gray-900" data-id="element-639" data-mp="21ts9111495dw">
                    Prediction
                  </h3>
                  <div className="text-xs text-green-600 mt-1" data-id="element-640" data-mp="2yhychkmaxrql">
                    3 players ≥65 · structural
                  </div>
                </div>
                <div className="text-2xl font-bold text-green-600" data-id="element-641" data-mp="2ppmixpvw5o6e">59.3</div>
              </div>
              <div className="space-y-3 mb-5" data-id="element-642" data-mp="2ks4kwujewtd6">
                <div className="flex items-center text-[13px]" data-id="element-643" data-mp="2kp8mz67ke4oq">
                  <div className="w-24 text-gray-600" data-id="element-644" data-mp="3i7s49yp0o6ut">Interception</div>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full mx-3 overflow-hidden" data-id="element-645" data-mp="3dfgvs7g7ncqn">
                    <div className="h-full bg-green-500" style={{
                    width: '69.6%'
                  }} data-id="element-646" data-mp="3kreul1v0jv35"></div>
                  </div>
                  <div className="w-8 text-right font-medium" data-id="element-647" data-mp="3pqhy2hvcht90">69.6</div>
                </div>
                <div className="flex items-center text-[13px]" data-id="element-648" data-mp="3unrl5qv9hsto">
                  <div className="w-24 text-gray-600" data-id="element-649" data-mp="2lsbucmfjgui">Timing</div>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full mx-3 overflow-hidden" data-id="element-650" data-mp="2j797leb4wvp">
                    <div className="h-full bg-amber-500" style={{
                    width: '56.2%'
                  }} data-id="element-651" data-mp="7isgqk7jmdk1"></div>
                  </div>
                  <div className="w-8 text-right font-medium" data-id="element-652" data-mp="hbhg4gptk0z0">56.2</div>
                </div>
                <div className="flex items-center text-[13px]" data-id="element-653" data-mp="m8hlsqirb1pl">
                  <div className="w-24 text-gray-600" data-id="element-654" data-mp="qb4knbduhmwa">Positioning</div>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full mx-3 overflow-hidden" data-id="element-655" data-mp="qcxnsgp91ozj">
                    <div className="h-full bg-red-500" style={{
                    width: '47.4%'
                  }} data-id="element-656" data-mp="vah2titru5sj"></div>
                  </div>
                  <div className="w-8 text-right font-medium" data-id="element-657" data-mp="dmf1cr8jdxel">47.4</div>
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded text-[13px] text-gray-700 leading-relaxed" data-id="element-658" data-mp="rdjudahefrfy">
                Opponents' intentions read at an elite level (Interception:
                69.6, 100th pct); intercepting lanes is a collective signature.
                Timing 85th - Positioning 33rd pct.
              </div>
            </div>

            {/* Resilience */}
            <div className="border border-gray-200 rounded-lg p-5" data-id="element-659" data-mp="8mivsnk6xxwx">
              <div className="flex justify-between items-start mb-6" data-id="element-660" data-mp="8kpkuw2jorj4">
                <div data-id="element-661" data-mp="3mxwgc13znjo">
                  <h3 className="text-base font-medium text-gray-900" data-id="element-662" data-mp="3meewwfdf166">
                    Resilience
                  </h3>
                  <div className="text-xs text-red-600 mt-1" data-id="element-663" data-mp="1f2uv4ryr1lu3">
                    1 player ≥65 · critical
                  </div>
                </div>
                <div className="text-2xl font-bold text-red-600" data-id="element-664" data-mp="2wzpy21eg34a2">44.9</div>
              </div>
              <div className="space-y-3 mb-5" data-id="element-665" data-mp="320dlye7dn502">
                <div className="flex items-center text-[13px]" data-id="element-666" data-mp="2nzt1zepq734z">
                  <div className="w-24 text-gray-600" data-id="element-667" data-mp="2o1dxp7ns0s4f">Impact</div>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full mx-3 overflow-hidden" data-id="element-668" data-mp="2sy4czsrrbjos">
                    <div className="h-full bg-amber-500" style={{
                    width: '63.2%'
                  }} data-id="element-669" data-mp="3go8e9thh37cm"></div>
                  </div>
                  <div className="w-8 text-right font-medium" data-id="element-670" data-mp="3ks6de46ssi5o">63.2</div>
                </div>
                <div className="flex items-center text-[13px]" data-id="element-671" data-mp="36yf5f08pt6c2">
                  <div className="w-24 text-gray-600" data-id="element-672" data-mp="3bwqdp6jd38s6">Error control</div>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full mx-3 overflow-hidden" data-id="element-673" data-mp="3i8b3kc6wlfy">
                    <div className="h-full bg-red-500" style={{
                    width: '44.6%'
                  }} data-id="element-674" data-mp="88ezbj2si3xo"></div>
                  </div>
                  <div className="w-8 text-right font-medium" data-id="element-675" data-mp="i45ema8xmyvj">44.6</div>
                </div>
                <div className="flex items-center text-[13px]" data-id="element-676" data-mp="d6voesdpyj6n">
                  <div className="w-24 text-gray-600" data-id="element-677" data-mp="85y9coo0a59d">Load balance</div>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full mx-3 overflow-hidden" data-id="element-678" data-mp="88arb1trkfmf">
                    <div className="h-full bg-red-500" style={{
                    width: '27.1%'
                  }} data-id="element-679" data-mp="39r12p850ho9"></div>
                  </div>
                  <div className="w-8 text-right font-medium" data-id="element-680" data-mp="3qjflrwroiua6">27.1</div>
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded text-[13px] text-gray-700 leading-relaxed" data-id="element-681" data-mp="3lm5qi0hlzoef">
                Contribution functional (Impact: 63.2) but struggles on load
                tolerance (27.1) and error management (44.6). Scouting priority:
                high Load Balance + Error Control profile.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: COGNITIVE CHEMISTRY */}
      <section className="mb-6" data-id="element-682" data-mp="1n5wx9eotso31">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-683" data-mp="1r4e3zx4c3wjx">
          <div className="flex items-center px-6 py-4 border-b border-gray-200" data-id="element-684" data-mp="1w3z265bok0vl">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-685" data-mp="2133bcvyltylx">
              Cognitive chemistry
            </h2>
          </div>
          <div className="px-6 py-5" data-id="element-686" data-mp="13gmdnplylizn">
            <div className="grid grid-cols-2 gap-6 mb-4" data-id="element-687" data-mp="13di4sexjtz2n">
              <div className="bg-green-50 border border-green-200 rounded-lg p-5" data-id="element-688" data-mp="18fpjhc9rjt8o">
                <h3 className="text-sm font-bold text-green-800 mb-2" data-id="element-689" data-mp="1ddhc68i8fjdx">
                  What works
                </h3>
                <p className="text-[13px] text-green-900 leading-relaxed" data-id="element-690" data-mp="1i595urwoq58a">
                  Attention reading{' '}
                  <strong className="font-bold" data-id="element-691" data-mp="kotlfxeps1sz">collective and dominant</strong>
                  . Systematic defensive interception at{' '}
                  <strong className="font-bold" data-id="element-692" data-mp="3f5ynigsipm2n">elite level</strong>. Reads
                  opponents{' '}
                  <strong className="font-bold" data-id="element-693" data-mp="3a7or62v24ffg">
                    faster than any team in the league
                  </strong>
                  .
                </p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-5" data-id="element-694" data-mp="3j83fzc78ccnp">
                <h3 className="text-sm font-bold text-red-800 mb-2" data-id="element-695" data-mp="3ja6fqny6pk5m">
                  What is missing
                </h3>
                <p className="text-[13px] text-red-900 leading-relaxed" data-id="element-696" data-mp="3t69j1x4rvou5">
                  Resilience concentrated on{' '}
                  <strong className="font-bold" data-id="element-697" data-mp="aqytqrzw7jcw">1 player</strong>. Load balance{' '}
                  <strong className="font-bold" data-id="element-698" data-mp="6pwwggpjdhdk">not distributed</strong> as a
                  collective trait. System suffers under{' '}
                  <strong className="font-bold" data-id="element-699" data-mp="kqptp9782qkd">
                    prolonged output demands
                  </strong>
                  .
                </p>
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-700" data-mp="zdg0ur7mnddv">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5" data-id="element-701" data-mp="14ayeguwlags7">
                Result
              </div>
              <p className="text-sm font-medium text-gray-800 leading-relaxed" data-id="element-702" data-mp="ph38bwntj5kp">
                Cognitive identity oriented toward{' '}
                <strong className="font-bold text-gray-900" data-id="element-703" data-mp="gm5d2ix77gq7">
                  reading and anticipation
                </strong>
                . Suffers when it must sustain output under prolonged load (
                <strong className="font-bold text-red-600" data-id="element-704" data-mp="6p0rolb6fpag">44.9</strong> —{' '}
                <strong className="font-bold text-red-600" data-id="element-705" data-mp="bn2gznz74kud">
                  only 1 player ≥65
                </strong>
                ).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: ROSTER EVOLUTION */}
      <section className="mb-6" data-id="element-706" data-mp="1r8y7muvvv45">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-707" data-mp="2ksr9dajc4vea">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-708" data-mp="2prc8t7qrjb1o">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-709" data-mp="2uqymfiai6tb0">
              Roster evolution
            </h2>
            <span className="text-[11px] text-gray-400 flex items-center" data-id="element-710" data-mp="2zqb9o45ae2e4">
              <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1" data-id="element-711" data-mp="33rdauqirkoiq"></span>
              structural target (≥65)
              <span className="inline-block w-2 h-2 rounded-full bg-amber-500 ml-3 mr-1" data-id="element-712" data-mp="38pokp1926z0y"></span>
              sufficient threshold (≥50)
            </span>
          </div>
          <div className="grid grid-cols-2 gap-6 px-6 py-5" data-id="element-713" data-mp="3ig9qp9r2b5tw">
            <div className="border border-green-200 bg-white rounded-lg overflow-hidden" data-id="element-714" data-mp="3ifqb3zovwtj1">
              <div className="bg-green-50 px-4 py-3 border-b border-green-100" data-id="element-715" data-mp="3ngnsia22ljjh">
                <h3 className="text-sm font-bold text-green-800" data-id="element-716" data-mp="3rhziigouw6s7">
                  Target profile
                </h3>
              </div>
              <div className="p-4 space-y-3" data-id="element-717" data-mp="1frhwbtbzuwrd">
                <div className="flex items-center text-sm" data-id="element-718" data-mp="1brhfy35c4bho">
                  <span className="w-24 font-medium text-gray-700" data-id="element-719" data-mp="1budhe1ge5iav">
                    Resilience
                  </span>
                  <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-bold mr-2" data-id="element-720" data-mp="11u33sfqg28v6">
                    ≥65
                  </span>
                  <span className="text-xs text-gray-500" data-id="element-721" data-mp="1zgsb953x1dof">
                    structural target
                  </span>
                </div>
                <div className="flex items-center text-sm" data-id="element-722" data-mp="1prikptoq032f">
                  <span className="w-24 font-medium text-gray-700" data-id="element-723" data-mp="1ppxlvrvdhxyr">
                    Load balance
                  </span>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-bold mr-2" data-id="element-724" data-mp="2i830lhtztie7">
                    ≥50
                  </span>
                  <span className="text-xs text-gray-500" data-id="element-725" data-mp="2dfs1uiazo2n8">
                    sufficient threshold
                  </span>
                </div>
                <div className="h-px bg-gray-100 my-2" data-id="element-726" data-mp="fl63mf7dutwn"></div>
                <div className="flex items-center text-sm" data-id="element-727" data-mp="fkmjqsy69vfz">
                  <span className="w-24 font-medium text-gray-700" data-id="element-728" data-mp="1ustq7axl54o">
                    Control
                  </span>
                  <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-bold mr-2" data-id="element-729" data-mp="6rt06jbp09wb">
                    ≥65
                  </span>
                  <span className="text-xs text-gray-500" data-id="element-730" data-mp="3t02g7ozpv19j">
                    structural target
                  </span>
                </div>
                <div className="flex items-center text-sm" data-id="element-731" data-mp="3f1uivfyrpxxu">
                  <span className="w-24 font-medium text-gray-700" data-id="element-732" data-mp="3jz47itec25km">
                    Discipline
                  </span>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-bold mr-2" data-id="element-733" data-mp="1da7s9fisg51w">
                    ≥45
                  </span>
                  <span className="text-xs text-gray-500" data-id="element-734" data-mp="1sc7kt4dbhqvj">
                    sufficient threshold
                  </span>
                </div>
              </div>
            </div>
            <div className="border border-gray-200 bg-white rounded-lg overflow-hidden" data-id="element-735" data-mp="1nb0dt7j00vpx">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-100" data-id="element-736" data-mp="215t14s54rg72">
                <h3 className="text-sm font-bold text-gray-800" data-id="element-737" data-mp="1wczwe49nw7uz">Priority</h3>
              </div>
              <div className="p-4" data-id="element-738" data-mp="2b1e4oupukoix">
                <ul className="space-y-2.5 text-sm text-gray-700" data-id="element-739" data-mp="2b4ihgghdosl7">
                  <li className="flex items-start" data-id="element-740" data-mp="2632v8l3gsg18">
                    <span className="text-green-600 font-bold mr-2 mt-0.5" data-id="element-741" data-mp="2k33vpzfthlg6">
                      +
                    </span>
                    <span data-id="element-742" data-mp="2g2tt8wr8hd62">
                      <strong className="font-bold text-gray-900" data-id="element-743" data-mp="2g2jyywz5ojuk">
                        PRESSURE HOLDER
                      </strong>{' '}
                      or{' '}
                      <strong className="font-bold text-gray-900" data-id="element-744" data-mp="lostm9nv0m4x">
                        IMPACT STABILIZER
                      </strong>{' '}
                      — the system needs{' '}
                      <strong className="font-bold" data-id="element-745" data-mp="s382rhly7ihr">
                        endurance and load tolerance
                      </strong>
                      , not more of what it already has.
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-746" data-mp="112lg4ld70r7r">
                    <span className="text-red-500 font-bold mr-2 mt-0.5" data-id="element-747" data-mp="4f86611ygxv9">
                      ✕
                    </span>
                    <span data-id="element-748" data-mp="8gjw67oqrl3z">
                      Do not acquire more{' '}
                      <strong className="font-bold" data-id="element-749" data-mp="dev9wti5qfrh">ATTENTION</strong> profiles
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-750" data-mp="i7ej7e9taoqh">
                    <span className="text-red-500 font-bold mr-2 mt-0.5" data-id="element-751" data-mp="3gwdduput72mm">
                      ✕
                    </span>
                    <span data-id="element-752" data-mp="3ltvfdv6hyfkl">
                      Do not acquire{' '}
                      <strong className="font-bold" data-id="element-753" data-mp="3eb5lzd42yv6x">
                        redundant floor readers
                      </strong>
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: COGNITIVE PROFILE FULL ROSTER */}
      <section data-id="element-754" data-mp="30aut64j7n6j4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-755" data-mp="30bcz5svocz48">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-756" data-mp="2ve4ijwq6d353">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-757" data-mp="2qk8np357jwe5">
              Cognitive profile · Full roster
            </h2>
            <span className="text-[11px] text-gray-400" data-id="element-758" data-mp="2lkdrql6ksgm8">
              ≥65 structural · 50–64 moderate · &lt;50 low · * key contributor
            </span>
          </div>
          <div className="overflow-x-auto" data-id="element-759" data-mp="2cliptlakdsoq">
            <table className="w-full text-left text-sm" data-id="element-760" data-mp="27n7ef64mfa4e">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-500 font-semibold" data-id="element-761" data-mp="27nqwcmu9tyif">
                <tr data-id="element-762" data-mp="1y1pon8w2o67b">
                  <th className="px-4 py-3" data-id="element-763" data-mp="220or8mubzqvl">Player</th>
                  <th className="px-4 py-3 w-16" data-id="element-764" data-mp="222hv5udk7wzw">Pos</th>
                  <th className="px-4 py-3 w-16" data-id="element-765" data-mp="1o1ozh6biuvv7">Min</th>
                  <th className="px-4 py-3 w-20 text-center" data-id="element-766" data-mp="1o39yb84vd0yv">CTRL</th>
                  <th className="px-4 py-3 w-20 text-center" data-id="element-767" data-mp="1t11nvvi1kazw">ATT</th>
                  <th className="px-4 py-3 w-20 text-center" data-id="element-768" data-mp="2gpb66ng1qgma">PRED</th>
                  <th className="px-4 py-3 w-20 text-center" data-id="element-769" data-mp="2gs744brw95aq">RES</th>
                  <th className="px-4 py-3" data-id="element-770" data-mp="2loeceb7risvu">Archetype</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-771" data-mp="26zi36atyz9tn">
                {roster.map((player, idx) => <tr key={idx} className="hover:bg-gray-50 transition-colors" data-id="element-772" data-mp="2bxtd0lk9lkbv" data-mpi={idx}>
                    <td className="px-4 py-2.5" data-id="element-773" data-mp="2zofdnrrxw2bu">
                      <span className={player.key ? 'font-bold text-gray-900' : 'font-medium text-gray-700'} data-id="element-774" data-mp="2zmvtvr72r95b">
                        {player.name}
                        {player.key ? '*' : ''}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-500" data-id="element-775" data-mp="o0agg1j962xk">{player.pos}</td>
                    <td className="px-4 py-2.5 text-gray-500" data-id="element-776" data-mp="11t9us07atr3j">{player.min}</td>

                    <td className="px-4 py-2.5 text-center" data-id="element-777" data-mp="xr53v988j8kl">
                      {player.ctrl !== '-' ? <div className="flex items-center justify-center gap-1.5" data-id="element-778" data-mp="xt9fkdcdlbvf">
                          <div className={`w-2 h-2 rounded-full ${getDotColor(player.ctrl)}`} data-id="element-779" data-mp="a1ubog4yami2"></div>
                          <span className="text-[13px] font-medium text-gray-700" data-id="element-780" data-mp="54bzmgvmzr5u">
                            {player.ctrl}
                          </span>
                        </div> : <span className="text-gray-300" data-id="element-781" data-mp="e2w8yrfb4ohe">-</span>}
                    </td>
                    <td className="px-4 py-2.5 text-center" data-id="element-782" data-mp="1ugw6xwule420">
                      {player.att !== '-' ? <div className="flex items-center justify-center gap-1.5" data-id="element-783" data-mp="1pikz1rnuy3mf">
                          <div className={`w-2 h-2 rounded-full ${getDotColor(player.att)}`} data-id="element-784" data-mp="1pkoynxeq4ggw"></div>
                          <span className="text-[13px] font-medium text-gray-700" data-id="element-785" data-mp="29imaa0bwhmr0">
                            {player.att}
                          </span>
                        </div> : <span className="text-gray-300" data-id="element-786" data-mp="2ijkh0qdq48da">-</span>}
                    </td>
                    <td className="px-4 py-2.5 text-center" data-id="element-787" data-mp="2ncncj4229rw4">
                      {player.pred !== '-' ? <div className="flex items-center justify-center gap-1.5" data-id="element-788" data-mp="2sbq5vr8oi9o9">
                          <div className={`w-2 h-2 rounded-full ${getDotColor(player.pred)}`} data-id="element-789" data-mp="2x9i9hiiork8a"></div>
                          <span className="text-[13px] font-medium text-gray-700" data-id="element-790" data-mp="328l4sb8v6c32">
                            {player.pred}
                          </span>
                        </div> : <span className="text-gray-300" data-id="element-791" data-mp="368lqmgyalpk5">-</span>}
                    </td>
                    <td className="px-4 py-2.5 text-center" data-id="element-792" data-mp="17wwkrunrzjbt">
                      {player.res !== '-' ? <div className="flex items-center justify-center gap-1.5" data-id="element-793" data-mp="1bx72g4kxjy6i">
                          <div className={`w-2 h-2 rounded-full ${getDotColor(player.res)}`} data-id="element-794" data-mp="14e7i9wpna2m3"></div>
                          <span className="text-[13px] font-medium text-gray-700" data-id="element-795" data-mp="zledj8u6eua0">
                            {player.res}
                          </span>
                        </div> : <span className="text-gray-300" data-id="element-796" data-mp="qm142g61y5pa">-</span>}
                    </td>

                    <td className="px-4 py-2.5" data-id="element-797" data-mp="1nc1pq6b4k62d">
                      {player.min <= 5 ? <span className="text-[12px] text-gray-400 italic" data-id="element-798" data-mp="1nb8rjtq8eovu">
                          {player.archetype}
                        </span> : <span className="inline-flex items-center px-2 py-1 rounded bg-gray-100 border border-gray-200 text-[11px] font-medium text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors" data-id="element-799" data-mp="1ebbwnad90tsc">
                          {player.archetype}
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>;
};
export default TeamNeuro;